import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import styles from "./Modal.module.css";

interface ModalProps {
	isOpen: boolean;
	onClose: () => void;
	title?: string;
	children: React.ReactNode;
	maxWidth?: number | string;
	closeOnOverlayClick?: boolean;
}

export function Modal({
	isOpen,
	onClose,
	title,
	children,
	maxWidth,
	closeOnOverlayClick = true,
}: ModalProps) {
	const { t } = useTranslation();
	const overlayRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		function handleKey(e: KeyboardEvent) {
			if (e.key === "Escape") onClose();
		}
		if (isOpen) {
			document.addEventListener("keydown", handleKey);
			document.body.style.overflow = "hidden";
		}
		return () => {
			document.removeEventListener("keydown", handleKey);
			document.body.style.overflow = "";
		};
	}, [isOpen, onClose]);

	if (!isOpen) return null;

	return (
		<div
			ref={overlayRef}
			className={styles.overlay}
			onClick={(e) => {
				if (closeOnOverlayClick && e.target === overlayRef.current) onClose();
			}}
		>
			<div
				className={styles.content}
				role="dialog"
				aria-modal="true"
				style={maxWidth ? { maxWidth } : undefined}
			>
				{title && (
					<div className={styles.header}>
						<h2 className={styles.title}>{title}</h2>
						<button
							type="button"
							className={styles.close}
							onClick={onClose}
							aria-label={t("close")}
						>
							<X size={18} />
						</button>
					</div>
				)}
				<div className={styles.body}>{children}</div>
			</div>
		</div>
	);
}
